// src/components/BottomNav.js
import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useGuardedNavigate } from "../utils/navGuard";
import { useAuth } from "../context/AuthContext";
import { db } from "../lib/firebase";
import { doc, getDoc } from "firebase/firestore";
import styled from "styled-components";
import theme from "../central/theme";
import {
  Users,
  Star,
  Trophy,
  Shield,
  MoreHorizontal,
  ChevronRight,
  LayoutDashboard,
  BarChart2,
  TrendingUp,
  Calendar,
  Settings,
  CheckSquare,
} from "lucide-react";

// ── Module-level user doc cache (mirrors LeagueNav pattern) ──────────────
// Keyed by `${uid}:${leagueID}` so admin status is cached per user per league.
const userAdminCache = new Map();

// ── Module-level league season/GW cache ──────────────────────────────────
// Keyed by leagueID only — this data isn't user-specific.
const leagueSeasonGWCache = new Map();

// ── Styled components ────────────────────────────────────────────────────────

const Bar = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #ffffff;
  border-top: 1px solid #E0E0E0;
  display: flex;
  flex-direction: column;
  z-index: 900;

  @media (min-width: 768px) {
    display: none;
  }
`;

/* 60px content zone — icons are always centred here */
const TabRow = styled.div`
  height: 60px;
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
`;

/* Safe area zone — pure background fill, no content */
const SafeAreaFill = styled.div`
  height: env(safe-area-inset-bottom);
  min-height: 0px;
  background-color: #ffffff;
`;

const Tab = styled.button`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  background: none;
  border: none;
  padding: 0 0 8px;
  cursor: pointer;
  color: ${({ $active }) => ($active ? theme.colors.primary : "#888888")};
  font-family: ${theme.typography.fontFamily.body};
  font-size: 10px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  transition: color 120ms ease;

  svg {
    width: 22px;
    height: 22px;
    stroke-width: 1.75;
  }
`;

// ── More sheet ────────────────────────────────────────────────────────────────

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 950;
`;

const Sheet = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #ffffff;
  border-radius: 20px 20px 0 0;
  padding: 0 0 env(safe-area-inset-bottom);
  z-index: 960;
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.12);
`;

const SheetHandle = styled.div`
  width: 40px;
  height: 4px;
  background: #cccccc;
  border-radius: 2px;
  margin: 12px auto 6px;
`;

const SheetItem = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 16px 20px;
  background: none;
  border: none;
  border-top: 1px solid ${theme.colors.border};
  font-family: ${theme.typography.fontFamily.body};
  font-size: 1rem;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  color: ${({ $active }) => ($active ? theme.colors.primary : theme.colors.text)};
  cursor: pointer;
  text-align: left;

  &:first-of-type {
    border-top: none;
  }
`;

const SheetItemLabel = styled.span`
  flex: 1;
`;

// ── Active icon helpers ───────────────────────────────────────────────────────
// lucide-react icons accept fill/stroke props directly.
// For icons where a solid fill reads well (Star, Trophy, Shield), we pass
// fill="currentColor" and reduce the stroke to 0 when active.
// For Users and MoreHorizontal the outline colour change is the right treatment.

function PickIcon({ active }) {
  return <Users fill={active ? theme.colors.primary : "none"} strokeWidth={active ? 0.5 : 1.75} />;
}

function PointsIcon({ active }) {
  return (
    <Star
      fill={active ? "currentColor" : "none"}
      strokeWidth={active ? 0 : 1.75}
    />
  );
}

function TableIcon({ active }) {
  return (
    <Trophy
      fill={active ? "currentColor" : "none"}
      strokeWidth={active ? 0 : 1.75}
    />
  );
}

function SquadIcon({ active }) {
  return (
    <Shield
      fill={active ? "currentColor" : "none"}
      strokeWidth={active ? 0 : 1.75}
    />
  );
}

function AvailabilityIcon({ active }) {
  return <CheckSquare fill={active ? "currentColor" : "none"} strokeWidth={active ? 0.5 : 1.75} />;
}

function MoreIcon({ active }) {
  return <MoreHorizontal fill={active ? "currentColor" : "none"} strokeWidth={active ? 0 : 1.75} />;
}

// ── Component ────────────────────────────────────────────────────────────────

export default function BottomNav() {
  const { leagueID: paramLeagueID } = useParams();
  const navigate = useGuardedNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();

  const [isAdmin, setIsAdmin] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [leagueSeasonGW, setLeagueSeasonGW] = useState({ currentSeasonID: null, currentGW: null });

  // Fetch current user's doc for admin check (cached).
  // Admin status lives on the user doc under roles: { [leagueID]: "admin" }.
  useEffect(() => {
    if (!paramLeagueID || !currentUser) {
      setIsAdmin(false);
      return;
    }
    const cacheKey = `${currentUser.uid}:${paramLeagueID}`;
    if (userAdminCache.has(cacheKey)) {
      setIsAdmin(userAdminCache.get(cacheKey));
      return;
    }
    (async () => {
      try {
        const snap = await getDoc(doc(db, "users", currentUser.uid));
        const admin =
          snap.exists() && snap.data().roles?.[paramLeagueID] === "admin";
        userAdminCache.set(cacheKey, admin);
        setIsAdmin(admin);
      } catch (e) {
        console.error("BottomNav: failed to fetch user roles", e);
      }
    })();
  }, [paramLeagueID, currentUser]);

  // Fetch the league doc's currentSeasonID/currentGW (cached; not user-specific).
  // Used to hide "Dream Team" during S1 GW1, before anything's been scored.
  useEffect(() => {
    if (!paramLeagueID) {
      setLeagueSeasonGW({ currentSeasonID: null, currentGW: null });
      return;
    }
    if (leagueSeasonGWCache.has(paramLeagueID)) {
      setLeagueSeasonGW(leagueSeasonGWCache.get(paramLeagueID));
      return;
    }
    (async () => {
      try {
        const snap = await getDoc(doc(db, "leagues", paramLeagueID));
        const data = snap.exists()
          ? {
              currentSeasonID: snap.data().currentSeasonID || null,
              currentGW: snap.data().currentGW || null,
            }
          : { currentSeasonID: null, currentGW: null };
        leagueSeasonGWCache.set(paramLeagueID, data);
        setLeagueSeasonGW(data);
      } catch (e) {
        console.error("BottomNav: failed to fetch league season/GW", e);
      }
    })();
  }, [paramLeagueID]);

  // Close sheet on navigation
  useEffect(() => {
    setSheetOpen(false);
  }, [location.pathname]);

  // Don't render if no leagueID in the URL
  if (!paramLeagueID) return null;

  const isActive = (segment) => location.pathname.includes(`/${segment}/`);

  const go = (path) => {
    if (location.pathname !== path) navigate(path);
  };

  // Hide "Dream Team" during S1 GW1 — nothing's been scored yet at that point,
  // so the page has nothing useful to show (see DreamTeam.js's own S1/GW1 guard).
  const isS1GW1 =
    leagueSeasonGW.currentSeasonID === "S1" && leagueSeasonGW.currentGW === "GW1";

  // More sheet always shows all secondary items.
  // For admins: Table goes into the sheet (Manage is in the bar).
  // For non-admins: Manage never appears.
  const sheetItems = [
    { label: "League",       path: `/league/${paramLeagueID}`,          segment: "league",       Icon: LayoutDashboard },
    { label: "Squad",        path: `/team/${paramLeagueID}`,            segment: "team",         Icon: Shield },
    { label: "Statistics",   path: `/statistics/${paramLeagueID}`,      segment: "statistics",   Icon: BarChart2 },
    { label: "Prices",     path: `/playerprices/${paramLeagueID}`, segment: "playerprices", Icon: TrendingUp },
    { label: "Matches",    path: `/matches/${paramLeagueID}/GW1`,  segment: "matches",      Icon: Calendar },
    ...(isS1GW1
      ? []
      : [{ label: "Dream Team", path: `/dreamteam/${paramLeagueID}`, segment: "dreamteam", Icon: Star }]),
    ...(isAdmin
      ? [{ label: "Table", path: `/leaderboard/${paramLeagueID}`, segment: "leaderboard", Icon: Trophy }]
      : []),
  ];

  // "More" tab is active when the current page is one of the sheet items
  const moreActive = sheetItems.some((item) =>
    location.pathname.includes(`/${item.segment}/`)
  );

  const pickActive         = isActive("pickteam");
  const pointsActive       = isActive("yourpoints");
  const tableActive        = isActive("leaderboard");
  const manageActive       = isActive("admin");
  const squadActive        = isActive("team");
  const availabilityActive = isActive("availability");
  const moreTabActive      = moreActive || sheetOpen;

  return (
    <>
      <Bar>
        <TabRow>
          <Tab $active={pickActive} onClick={() => go(`/pickteam/${paramLeagueID}`)} aria-label="Pick Team">
            <PickIcon active={pickActive} />
            Pick
          </Tab>

          <Tab $active={pointsActive} onClick={() => go(`/yourpoints/${paramLeagueID}`)} aria-label="Points">
            <PointsIcon active={pointsActive} />
            Points
          </Tab>

          {isAdmin ? (
            <Tab $active={manageActive} onClick={() => go(`/admin/${paramLeagueID}`)} aria-label="Manage">
              <Settings
                size={22}
                fill={manageActive ? "currentColor" : "none"}
                strokeWidth={manageActive ? 0 : 1.75}
              />
              Manage
            </Tab>
          ) : (
            <Tab $active={tableActive} onClick={() => go(`/leaderboard/${paramLeagueID}`)} aria-label="Leaderboard">
              <TableIcon active={tableActive} />
              Table
            </Tab>
          )}

          <Tab $active={availabilityActive} onClick={() => go(`/availability/${paramLeagueID}`)} aria-label="Availability">
            <AvailabilityIcon active={availabilityActive} />
            Availability
          </Tab>

          <Tab $active={moreTabActive} onClick={() => setSheetOpen((o) => !o)} aria-label="More">
            <MoreIcon active={moreTabActive} />
            More
          </Tab>
        </TabRow>
        <SafeAreaFill />
      </Bar>

      {sheetOpen && (
        <>
          <Backdrop onClick={() => setSheetOpen(false)} />
          <Sheet>
            <SheetHandle />
            {sheetItems.map(({ segment, label, path, Icon }) => {
              const active = location.pathname.includes(`/${segment}/`);
              return (
                <SheetItem
                  key={segment}
                  $active={active}
                  onClick={() => { setSheetOpen(false); go(path); }}
                >
                  <Icon
                    size={20}
                    color={theme.colors.primary}
                    strokeWidth={1.75}
                  />
                  <SheetItemLabel>{label}</SheetItemLabel>
                  <ChevronRight size={16} color={theme.colors.primary} />
                </SheetItem>
              );
            })}
          </Sheet>
        </>
      )}
    </>
  );
}
